import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./useAuth";

export type TenantSlug = "dev" | "staging" | "pre-prod" | "prod";

export interface Tenant {
  id: string;
  slug: TenantSlug;
  name: string;
  order_prefix: string;
  eapi_env: string;
}

interface TenantContextType {
  activeTenant: TenantSlug;
  tenantId: string | null;
  eapiEnv: "dev" | "staging" | "pre-prod" | "prod";
  setActiveTenant: (slug: TenantSlug) => void;
  tenants: Tenant[];
  isLoading: boolean;
  tenantReady: boolean;
  /** True when the tenant list could not be resolved after retries. */
  tenantError: boolean;
  retryTenantLoad: () => void;
}

const TenantContext = createContext<TenantContextType>({
  activeTenant: "prod",
  tenantId: null,
  eapiEnv: "prod",
  setActiveTenant: () => {},
  tenants: [],
  isLoading: true,
  tenantReady: false,
  tenantError: false,
  retryTenantLoad: () => {},
});

const STORAGE_KEY = "atms_tenant_slug";

function readPersistedTenant(): TenantSlug {
  const val = localStorage.getItem(STORAGE_KEY);
  if (val === "dev" || val === "staging" || val === "pre-prod" || val === "prod") return val;
  return "prod";
}

/** Set the x-tenant-slug header on the Supabase client so PostgREST
 *  exposes it as current_setting('request.header.x-tenant-slug') for RLS.
 *  We mutate the SupabaseClient's internal `headers` object (not `.rest.headers`)
 *  because `.rest` is a getter that creates a new PostgrestClient each call. */
function setTenantHeader(slug: TenantSlug) {
  (supabase as any).headers["x-tenant-slug"] = slug;
}

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeTenant, setActiveTenantState] = useState<TenantSlug>(readPersistedTenant);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tenantReady, setTenantReady] = useState(false);
  const [tenantError, setTenantError] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);

  // Fetch tenants whenever the auth user changes (including the initial
  // session hydration). The previous version ran once on mount, before the
  // session was guaranteed to exist — a `permission denied for table tenants`
  // response left `tenantId` null for the whole session, which silently
  // disabled every tenant-scoped query and rendered pages as empty.
  //
  // Boot-path hardening: mobile resume can hand us an *expired* JWT (the
  // `token is expired` 403s seen on AOP-VLTK-00047). Awaiting getSession()
  // first lets supabase-js refresh the token before the very first PostgREST
  // call, so we never burn the boot attempt on a request that authenticates
  // as `anon`.
  useEffect(() => {
    setTenantHeader(activeTenant);
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const load = async (attempt: number) => {
      // Refreshes the access token when it is expired/near expiry.
      const { data: sessionData } = await supabase.auth.getSession();
      if (cancelled) return;

      // No session yet during initial hydration — wait for the auth listener
      // below rather than issuing an anon request that is guaranteed to 401.
      if (!sessionData?.session) {
        if (attempt < 3) {
          timer = setTimeout(() => load(attempt + 1), Math.min(500 * 2 ** attempt, 4000));
          return;
        }
        setTenantError(true);
        setTenantReady(false);
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase.from("tenants").select("*").order("slug");
      if (cancelled) return;
      if (!error && data && data.length > 0) {
        setTenants(data as Tenant[]);
        setTenantError(false);
        setTenantReady(true);
        setIsLoading(false);
        return;
      }
      if (attempt < 3) {
        // Backoff and retry — transient auth/session races resolve quickly.
        timer = setTimeout(() => load(attempt + 1), Math.min(500 * 2 ** attempt, 4000));
        return;
      }
      console.error("[Tenant] Failed to resolve tenants", error);
      setTenantError(true);
      setTenantReady(false);
      setIsLoading(false);
    };

    setIsLoading(true);
    load(0);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, reloadNonce]);

  const retryTenantLoad = useCallback(() => setReloadNonce((n) => n + 1), []);

  // Recover automatically instead of stranding the session with a null
  // tenantId: a token refresh or a tab/app resume re-attempts the load when
  // the tenant list never resolved.
  const tenantReadyRef = useRef(false);
  tenantReadyRef.current = tenantReady;

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (tenantReadyRef.current) return;
      if (event === "TOKEN_REFRESHED" || event === "SIGNED_IN" || event === "INITIAL_SESSION") {
        retryTenantLoad();
      }
    });
    const onResume = () => {
      if (document.visibilityState === "visible" && !tenantReadyRef.current) retryTenantLoad();
    };
    document.addEventListener("visibilitychange", onResume);
    return () => {
      sub.subscription.unsubscribe();
      document.removeEventListener("visibilitychange", onResume);
    };
  }, [retryTenantLoad]);


  // Track whether we've ever seen an authenticated user so we don't
  // wipe localStorage on the initial mount (user starts null before session restores)
  const hadUserRef = useRef(false);

  useEffect(() => {
    if (user) {
      hadUserRef.current = true;
    } else if (hadUserRef.current) {
      // User was authenticated and is now logged out — reset to prod
      localStorage.removeItem(STORAGE_KEY);
      setActiveTenantState("prod");
      setTenantHeader("prod");
    }
  }, [user]);

  const setActiveTenant = useCallback((slug: TenantSlug) => {
    localStorage.setItem(STORAGE_KEY, slug);
    setActiveTenantState(slug);
    setTenantHeader(slug);
    queryClient.invalidateQueries();
  }, [queryClient]);

  const currentTenant = tenants.find((t) => t.slug === activeTenant);

  const value = useMemo(
    () => ({
      activeTenant,
      tenantId: currentTenant?.id ?? null,
      eapiEnv: (currentTenant?.eapi_env ?? "prod") as "dev" | "staging" | "pre-prod" | "prod",
      setActiveTenant,
      tenants,
      isLoading,
      tenantReady,
      tenantError,
      retryTenantLoad,
    }),
    [activeTenant, currentTenant, setActiveTenant, tenants, isLoading, tenantReady, tenantError, retryTenantLoad]
  );

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  return useContext(TenantContext);
}
